import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth, useFechaOperacion } from "@/contexts/AuthContext";
import {
  PlusIcon, CheckCircleIcon, XCircleIcon, ClockIcon,
  PaperclipIcon, EyeIcon, FunnelIcon, ChartPieSliceIcon, WarningIcon, FilePdfIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const ALLOWED_MIMES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];

const fileToDataUrl = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

/**
 * Tab "Cobros RTC" del módulo 6.
 * Registro de pagos por el RTC con comprobante, autorización por admin, disparador de GRATIVA.
 */
export default function RTCPaymentsTab() {
  const { user, hasPermission } = useAuth();
  const fechaOp0 = useFechaOperacion();
  // RTC (alcance regional): el cobro solo se registra con la fecha corriente de la empresa
  const esRtc = Boolean(user?.effective?.alcance_regional);
  const fechaOp = { ...fechaOp0, fija: fechaOp0.fija || esRtc };
  // Solo el Administrador General aprueba, rechaza o devuelve cobros registrados por RTC
  const canAuthorize = Boolean(user?.effective?.is_admin || user?.role === "admin");
  const canRegister = hasPermission?.("payments.register_rtc") || ["admin", "sales", "salesplus", "accountant"].includes(user?.role);
  const canChangeTipo = ["admin", "accountant"].includes(user?.role) || hasPermission?.("payments.authorize") || hasPermission?.("payments.change_tipo_venta");

  const [tab, setTab] = useState(esRtc ? "mine" : "pending"); // pending | mine | all
  const [editingId, setEditingId] = useState(null);         // corrección de un cobro en borrador / devuelto
  const [extra, setExtra] = useState([]);                   // otras facturas del mismo cliente [{factura_id, monto}]
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [authModal, setAuthModal] = useState(null);
  const [desgloseModal, setDesgloseModal] = useState(null);
  const [viewerModal, setViewerModal] = useState(null);

  const [form, setForm] = useState({
    factura_id: "", monto: 0, metodo: "transferencia", referencia: "", banco: "",
    notas: "", rtc_id: "", es_venta_rtc: true, cuenta_id: "",
    comprobante_file: null, comprobante_data: "", comprobante_descripcion: "", fecha: todayYmd(),
  });
  const [authForm, setAuthForm] = useState({ cuenta_id: "", notas: "" });

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = tab === "mine" ? "/payments/mis-cobros"
        : tab === "all" ? "/payments"
        : "/payments/pendientes";
      const [p, inv, a] = await Promise.all([
        api.get(endpoint),
        api.get("/invoices?tipo=factura"),
        // el RTC no elige ni ve cuentas bancarias: la cuenta la asigna quien aprueba el cobro
        user?.effective?.alcance_regional ? Promise.resolve({ data: [] }) : api.get("/treasury/accounts").catch(() => ({ data: [] })),
      ]);
      setItems(p.data);
      setInvoices(inv.data.filter((i) => i.estado !== "anulada" && i.saldo_pendiente > 0));
      setAccounts(a.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const onPickFactura = (fid) => {
    const inv = invoices.find((i) => i.id === fid);
    setForm((f) => ({
      ...f,
      factura_id: fid,
      monto: inv ? inv.saldo_pendiente : 0,
      rtc_id: inv?.vendedor_rtc_id || "",
      es_venta_rtc: inv?.tipo_venta === "rtc",
    }));
  };

  const onPickComprobante = async (file) => {
    if (!file) return setForm((f) => ({ ...f, comprobante_file: null, comprobante_data: "" }));
    if (file.size > 5 * 1024 * 1024) { toast.error("Archivo > 5MB"); return; }
    if (file.type && !ALLOWED_MIMES.includes(file.type)) { toast.error("Solo imágenes (JPG/PNG/WEBP/HEIC) o PDF"); return; }
    const data = await fileToDataUrl(file);
    setForm((f) => ({ ...f, comprobante_file: file, comprobante_data: data }));
  };

  const [enviarAhora, setEnviarAhora] = useState(true);
  const resetForm = () => {
    setEditingId(null); setExtra([]);
    setForm({
      factura_id: "", monto: 0, metodo: "transferencia", referencia: "", banco: "",
      notas: "", rtc_id: "", es_venta_rtc: true, cuenta_id: "",
      comprobante_file: null, comprobante_data: "", comprobante_descripcion: "", fecha: todayYmd(),
    });
  };
  const corregir = (p) => {
    const apps = p.allocations?.length ? p.allocations : [{ factura_id: p.factura_id, monto: p.monto }];
    setEditingId(p.id);
    setForm({
      factura_id: apps[0].factura_id, monto: apps[0].monto, metodo: p.metodo, referencia: p.referencia || "", banco: p.banco || "",
      notas: p.notas || "", rtc_id: p.registrado_por_rtc_id || "", es_venta_rtc: p.es_venta_rtc !== false, cuenta_id: p.cuenta_id || "",
      comprobante_file: null, comprobante_data: "", comprobante_descripcion: "", fecha: String(p.fecha || "").slice(0, 10),
      tiene_comprobantes: (p.comprobantes || []).length,
    });
    setExtra(apps.slice(1).map((a) => ({ factura_id: a.factura_id, monto: a.monto })));
    setOpen(true);
  };
  const enviarPago = async (p) => {
    try { await api.post(`/payments/${p.id}/enviar`); toast.success("Cobro enviado a autorización"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const devolver = async (pay) => {
    // eslint-disable-next-line no-alert
    const motivo = prompt(`¿Qué debe corregir el RTC en el cobro ${pay.factura_numero}?`);
    if (!motivo || !motivo.trim()) return;
    try { await api.post(`/payments/${pay.id}/devolver`, { motivo }); toast.success("Cobro devuelto para corrección"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const recibo = async (pay) => {
    try {
      const res = await api.get(`/payments/${pay.id}/receipt-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const register = async (e) => {
    e.preventDefault();
    if (!form.factura_id) return toast.error("Selecciona la factura");
    const enviar = enviarAhora;
    if (enviar && !form.comprobante_data && !form.tiene_comprobantes) return toast.error("Adjunta el comprobante (foto o PDF). Sin comprobante no se puede enviar a autorización.");
    const aplicaciones = [{ factura_id: form.factura_id, monto: Number(form.monto) },
      ...extra.filter((x) => x.factura_id).map((x) => ({ factura_id: x.factura_id, monto: Number(x.monto) }))];
    try {
      const body = {
        aplicaciones, metodo: form.metodo,
        referencia: form.referencia, banco: form.banco, notas: form.notas,
        rtc_id: form.rtc_id, es_venta_rtc: form.es_venta_rtc, cuenta_id: form.cuenta_id,
        comprobante_data: form.comprobante_data,
        comprobante_descripcion: form.comprobante_descripcion,
        fecha: (fechaOp.fija ? fechaOp.hoy : form.fecha) || null, enviar,
      };
      if (editingId) await api.put(`/payments/rtc/${editingId}`, body);
      else await api.post("/payments/rtc", body);
      toast.success(enviar ? "Cobro enviado con comprobante. Pendiente de aprobación del Administrador General." : "Cobro guardado como borrador.");
      setOpen(false);
      setEditingId(null); setExtra([]);
      setForm({
        factura_id: "", monto: 0, metodo: "transferencia", referencia: "", banco: "",
        notas: "", rtc_id: "", es_venta_rtc: true, cuenta_id: "",
        comprobante_file: null, comprobante_data: "", comprobante_descripcion: "", fecha: todayYmd(),
      });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const regm = useModalForm({ value: { form, extra }, abierto: open, onClose: () => { setOpen(false); setEditingId(null); }, onSave: (e) => register(e) });
  const authm = useModalForm({ value: authForm, abierto: Boolean(authModal), onClose: () => setAuthModal(null), onSave: (e) => authorize(e),
                               mensaje: "¿Está seguro de que desea autorizar este cobro?" });
  const authorize = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/payments/${authModal.id}/autorizar`, authForm);
      toast.success("Cobro autorizado. CxC aplicada, tesorería actualizada y GRATIVA devengada.");
      setAuthModal(null); setAuthForm({ cuenta_id: "", notas: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const reject = async (pay) => {
    // eslint-disable-next-line no-alert
    const motivo = prompt(`Motivo del rechazo del cobro de L. ${fmtL(pay.monto).replace("L ", "")} (factura ${pay.factura_numero}):`);
    if (!motivo) return;
    try {
      await api.post(`/payments/${pay.id}/rechazar`, { motivo });
      toast.success("Cobro rechazado");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const verDesglose = async (pay) => {
    try {
      const { data } = await api.get(`/payments/${pay.id}/desglose`);
      setDesgloseModal({ ...data, pay });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const selectedInv = invoices.find((i) => i.id === form.factura_id);
  const pendCount = items.filter((p) => p.estado_autorizacion === "pendiente").length;

  return (
    <>
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-2 border-b border-border/60 flex-1">
          <SubTab active={tab === "pending"} onClick={() => setTab("pending")} icon={ClockIcon} testid="tab-pending">
            Pendientes {pendCount > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-agro-yellow/20 text-agro-yellow rounded">{pendCount}</span>}
          </SubTab>
          <SubTab active={tab === "mine"} onClick={() => setTab("mine")} icon={FunnelIcon} testid="tab-mine">Mis cobros</SubTab>
          {canAuthorize && (
            <SubTab active={tab === "all"} onClick={() => setTab("all")} icon={CheckCircleIcon} testid="tab-all">Histórico</SubTab>
          )}
        </div>
        {canRegister && (
          <button onClick={() => { resetForm(); setOpen(true); }} data-testid="register-payment-btn"
            className="ml-3 bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95">
            <PlusIcon size={16} weight="bold" /> Registrar cobro
          </button>
        )}
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Estado</th>
                <th className="p-3">Factura</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">RTC / Región</th>
                <th className="p-3">Método</th>
                <th className="p-3 text-right">Monto</th>
                <th className="p-3">Comprob.</th>
                <th className="p-3">Fecha</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="9" className="p-12 text-center text-text-muted">
                  {tab === "pending" ? "Sin pagos pendientes" : tab === "mine" ? "Aún no registras cobros" : "Sin cobros"}
                </td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`payment-row-${p.id}`}>
                  <td className="p-3 pl-5">
                    <EstadoBadge estado={p.estado_autorizacion} motivo={p.estado_autorizacion === "devuelto" ? p.devolucion_motivo : p.rechazo_motivo} />
                  </td>
                  <td className="p-3 text-xs font-mono text-agro-green">{p.factura_numero}</td>
                  <td className="p-3 text-sm">
                    {p.cliente_nombre || "—"}
                    <div className="text-[10px] text-text-muted">{p.cliente_rtn || "—"} · {p.nivel_precio_cliente && <span className="text-agro-yellow">{p.nivel_precio_cliente}</span>}</div>
                  </td>
                  <td className="p-3 text-xs">
                    {p.registrado_por_rtc_nombre || <span className="text-text-muted italic">directo</span>}
                    {p.region && <div className="text-[10px] text-text-muted">{p.region}</div>}
                  </td>
                  <td className="p-3 text-xs uppercase">{p.metodo}</td>
                  <td className="p-3 text-right text-sm font-medium">{fmtL(p.monto)}</td>
                  <td className="p-3">
                    {p.comprobantes?.length > 0 ? (
                      <button onClick={() => setViewerModal({ comps: p.comprobantes, pago: p })}
                        className="inline-flex items-center gap-1 text-agro-green hover:underline text-xs"
                        data-testid={`view-comp-${p.id}`}>
                        <PaperclipIcon size={12} /> {p.comprobantes.length}
                      </button>
                    ) : <span className="text-[10px] text-text-muted">—</span>}
                  </td>
                  <td className="p-3 text-xs text-text-secondary">
                    {fmtDateTime(p.fecha_recepcion || p.fecha)}
                    {p.fecha_aprobacion && <div className="text-[10px] text-text-muted">Aprobado {fmtDateTime(p.fecha_aprobacion)}</div>}
                  </td>
                  <td className="p-3 pr-5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => verDesglose(p)} className="p-1.5 text-text-muted hover:text-agro-green" title="Ver desglose" data-testid={`desglose-${p.id}`}>
                        <ChartPieSliceIcon size={16} />
                      </button>
                      {p.created_by === user?.email && ["borrador", "devuelto"].includes(p.estado_autorizacion) && (
                        <>
                          <button onClick={() => corregir(p)} className="px-2 py-1 text-[10px] uppercase border border-border rounded" data-testid={`fix-${p.id}`}>Corregir</button>
                          <button onClick={() => enviarPago(p)} className="px-2 py-1 text-[10px] uppercase bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded" data-testid={`send-${p.id}`}>Enviar</button>
                        </>
                      )}
                      {["autorizado", "aplicado"].includes(p.estado_autorizacion) && (
                        <button onClick={() => recibo(p)} className="p-1.5 text-text-muted hover:text-agro-green" title="Recibo oficial (PDF)" data-testid={`receipt-${p.id}`}>
                          <FilePdfIcon size={16} />
                        </button>
                      )}
                      {p.estado_autorizacion === "pendiente" && canAuthorize && (
                        <>
                          <button onClick={() => devolver(p)} className="px-2 py-1 text-[10px] uppercase text-orange-400 border border-orange-500/40 rounded" title="Devolver para corrección" data-testid={`return-${p.id}`}>
                            Devolver
                          </button>
                          <button onClick={() => { setAuthModal(p); setAuthForm({ cuenta_id: p.cuenta_id || "", notas: "" }); }}
                            className="p-1.5 text-agro-green hover:bg-agro-green/10 rounded" title="Autorizar" data-testid={`auth-${p.id}`}>
                            <CheckCircleIcon size={16} weight="bold" />
                          </button>
                          <button onClick={() => reject(p)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" title="Rechazar" data-testid={`reject-${p.id}`}>
                            <XCircleIcon size={16} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal registrar */}
      {open && (
        <Modal title={editingId ? "Corregir cobro" : "Registrar cobro"} onClose={() => { setOpen(false); setEditingId(null); }} m={regm} testid="register-modal" wide>
          <form onSubmit={regm.guardar} className="space-y-3 text-sm">
            <div>
              <Lbl>Factura *</Lbl>
              <select required value={form.factura_id} onChange={(e) => onPickFactura(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="reg-factura">
                <option value="">— Selecciona factura con saldo —</option>
                {invoices.map((i) => <option key={i.id} value={i.id}>{i.numero} — {i.cliente_nombre} (saldo {fmtL(i.saldo_pendiente)})</option>)}
              </select>
              {selectedInv && (
                <div className="mt-1 grid grid-cols-2 gap-2 text-xs bg-app/40 border border-border/40 rounded-md p-2">
                  <div><span className="text-text-muted">Total:</span> <strong>{fmtL(selectedInv.total)}</strong></div>
                  <div><span className="text-text-muted">Saldo:</span> <strong className="text-agro-yellow">{fmtL(selectedInv.saldo_pendiente)}</strong></div>
                  <div className="flex items-center gap-1"><span className="text-text-muted">Tipo:</span>
                    <select value={form.es_venta_rtc ? "rtc" : "directa"} disabled={!canChangeTipo}
                      onChange={(e) => setForm({ ...form, es_venta_rtc: e.target.value === "rtc" })}
                      data-testid="reg-tipo-venta"
                      className="bg-app border border-border rounded px-1 py-0.5 text-xs disabled:opacity-80">
                      <option value="directa">Venta directa</option>
                      <option value="rtc">Venta por RTC</option>
                    </select>
                  </div>
                  <div><span className="text-text-muted">Región:</span> <strong data-testid="reg-region">{selectedInv.region || "—"}</strong></div>
                  <div><span className="text-text-muted">Vendedor RTC:</span> <strong>{selectedInv.vendedor || "—"}</strong></div>
                  <div><span className="text-text-muted">Nivel precio:</span> <strong className="text-agro-green" data-testid="reg-nivel-precio">{selectedInv.nivel_precio_cliente || selectedInv.nivel_precio || "—"}</strong></div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Lbl>Monto *</Lbl>
                <input type="number" required step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="reg-monto" />
              </div>
              <div>
                <Lbl>Método *</Lbl>
                <select value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="reg-metodo">
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="cheque">Cheque</option>
                  <option value="deposito">Depósito</option>
                  <option value="tarjeta">Tarjeta</option>
                </select>
              </div>
              {!esRtc && <div>
                <Lbl>Cuenta a depositar</Lbl>
                <select value={form.cuenta_id} onChange={(e) => setForm({ ...form, cuenta_id: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2">
                  <option value="">— Auto por método —</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} - {a.nombre}</option>)}
                </select>
              </div>}
            </div>
            {selectedInv && (
              <div className="border border-border/60 rounded-md p-2 space-y-1" data-testid="reg-otras-facturas">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] uppercase tracking-wider text-text-muted">Otras facturas del mismo cliente (opcional)</span>
                  <button type="button" onClick={() => setExtra([...extra, { factura_id: "", monto: 0 }])} className="text-xs text-agro-green">+ Agregar factura</button>
                </div>
                {extra.map((x, i) => (
                  <div key={i} className="flex gap-2">
                    <select value={x.factura_id} onChange={(e) => {
                      const inv = invoices.find((v) => v.id === e.target.value);
                      setExtra(extra.map((y, j) => (j === i ? { factura_id: e.target.value, monto: inv ? inv.saldo_pendiente : 0 } : y)));
                    }} className="flex-1 bg-app border border-border rounded px-2 py-1 text-xs">
                      <option value="">— Factura —</option>
                      {invoices.filter((v) => v.cliente_id === selectedInv.cliente_id && v.id !== form.factura_id).map((v) => (
                        <option key={v.id} value={v.id}>{v.numero} (saldo {fmtL(v.saldo_pendiente)})</option>
                      ))}
                    </select>
                    <input type="number" step="0.01" value={x.monto} onChange={(e) => setExtra(extra.map((y, j) => (j === i ? { ...y, monto: e.target.value } : y)))}
                      className="w-28 bg-app border border-border rounded px-2 py-1 text-xs text-right" />
                    <button type="button" onClick={() => setExtra(extra.filter((_, j) => j !== i))} className="text-xs text-red-400 px-1">×</button>
                  </div>
                ))}
                {extra.length > 0 && (
                  <div className="text-xs text-right">Total del cobro: <strong>{fmtL(Number(form.monto || 0) + extra.reduce((t, x) => t + Number(x.monto || 0), 0))}</strong></div>
                )}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Lbl>Fecha del pago *</Lbl>
                <input type="date" required value={fechaOp.fija ? fechaOp.hoy : form.fecha} max={todayYmd()} disabled={fechaOp.fija}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="reg-fecha"
                  title={fechaOp.fija ? "La fecha la fija el servidor: fecha corriente de la empresa" : undefined} />
              </div>
              <div>
                <Lbl>Referencia</Lbl>
                <input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" placeholder="TRF-12345" />
              </div>
              <div>
                <Lbl>Banco / Emisor</Lbl>
                <input value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" placeholder="BAC, Atlántida, etc." />
              </div>
            </div>

            <div className="border border-dashed border-agro-yellow/40 bg-agro-yellow/5 p-3 rounded-md">
              <Lbl>Comprobante (foto o PDF) *</Lbl>
              <div className="flex items-center gap-3">
                <input type="file" accept="image/*,application/pdf" onChange={(e) => onPickComprobante(e.target.files?.[0])}
                  className="text-xs flex-1" data-testid="reg-comprobante" />
                {form.comprobante_data && (
                  <span className="text-[10px] text-agro-green flex items-center gap-1">
                    <CheckCircleIcon size={12} weight="fill" /> {form.comprobante_file?.name?.slice(0, 30) || "Adjuntado"}
                  </span>
                )}
              </div>
              {form.comprobante_data?.startsWith("data:image") && (
                <img src={form.comprobante_data} alt="prev" className="mt-2 max-h-40 rounded border border-border" />
              )}
              {form.comprobante_data?.startsWith("data:application/pdf") && (
                <div className="mt-2 text-xs flex items-center gap-1 text-text-secondary"><FilePdfIcon size={14} /> PDF adjunto</div>
              )}
              <input value={form.comprobante_descripcion} onChange={(e) => setForm({ ...form, comprobante_descripcion: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-2 py-1 text-xs mt-2" placeholder="Descripción breve del comprobante (opcional)" />
              <div className="text-[10px] text-agro-yellow mt-1 flex items-center gap-1">
                <WarningIcon size={11} /> Sin comprobante el cobro NO se podrá autorizar.
              </div>
            </div>

            <div>
              <Lbl>Notas</Lbl>
              <textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" placeholder="Observaciones…" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={regm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
              <button type="submit" disabled={regm.saving} onClick={() => setEnviarAhora(false)} className="px-4 py-2 text-sm border border-border rounded-md" data-testid="reg-draft">
                Guardar borrador
              </button>
              <button type="submit" disabled={regm.saving} onClick={() => setEnviarAhora(true)} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md" data-testid="reg-submit">
                Enviar a autorización
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal autorizar */}
      {authModal && (
        <Modal title={`Autorizar cobro · ${authModal.factura_numero}`} onClose={() => setAuthModal(null)} m={authm} testid="auth-modal">
          <form onSubmit={authm.guardar} className="space-y-3 text-sm">
            <InfoRow l="Cliente" v={authModal.cliente_nombre} />
            <InfoRow l="Monto" v={fmtL(authModal.monto)} highlight />
            <InfoRow l="Método" v={authModal.metodo} />
            <InfoRow l="Referencia" v={authModal.referencia || "—"} />
            <InfoRow l="RTC" v={authModal.registrado_por_rtc_nombre || "—"} />
            <InfoRow l="Región / Nivel" v={`${authModal.region || "—"} · ${authModal.nivel_precio_cliente || "—"}`} />

            <div className="border border-border rounded-md p-2">
              <div className="text-[10px] uppercase text-text-muted mb-1">Comprobantes ({authModal.comprobantes?.length || 0})</div>
              <div className="flex gap-2 overflow-x-auto">
                {authModal.comprobantes?.map((c, i) => (
                  <button key={i} type="button" onClick={() => setViewerModal({ comps: authModal.comprobantes, pago: authModal, initial: i })}
                    className="flex-shrink-0 border border-border rounded p-1 hover:border-agro-green">
                    {c.url?.startsWith("data:image") ? (
                      <img src={c.url} alt="" className="h-20 w-auto rounded" />
                    ) : (
                      <div className="h-20 w-20 flex items-center justify-center text-text-secondary"><FilePdfIcon size={28} /></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Lbl>Cuenta donde se acreditó *</Lbl>
              <select required value={authForm.cuenta_id} onChange={(e) => setAuthForm({ ...authForm, cuenta_id: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" data-testid="auth-cuenta">
                <option value="">— Selecciona cuenta —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} - {a.nombre} (saldo {fmtL(a.saldo_actual || 0)})</option>)}
              </select>
            </div>
            <div>
              <Lbl>Notas de autorización</Lbl>
              <input value={authForm.notas} onChange={(e) => setAuthForm({ ...authForm, notas: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={authm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
              <button type="submit" disabled={authm.saving} className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md flex items-center gap-2" data-testid="auth-submit">
                <CheckCircleIcon size={14} weight="bold" /> Autorizar y aplicar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal desglose */}
      {desgloseModal && (
        <Modal title={`Desglose del pago · ${desgloseModal.factura_numero}`} onClose={() => setDesgloseModal(null)} testid="desglose-modal" wide>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Por categoría</h4>
              <div className="space-y-1">
                {desgloseModal.por_categoria.length === 0 && <div className="text-xs text-text-muted">Sin datos</div>}
                {desgloseModal.por_categoria.map((c, i) => (
                  <div key={i} className="flex justify-between p-2 bg-app/40 rounded border border-border/40" data-testid={`des-cat-${i}`}>
                    <span>{c.categoria}</span>
                    <span className="font-medium text-agro-green">{fmtL(c.monto)} <span className="text-[10px] text-text-muted">({c.porcentaje}%)</span></span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Por proveedor</h4>
              <div className="space-y-1">
                {desgloseModal.por_proveedor.length === 0 && <div className="text-xs text-text-muted">Sin datos</div>}
                {desgloseModal.por_proveedor.map((p, i) => (
                  <div key={i} className="flex justify-between p-2 bg-app/40 rounded border border-border/40" data-testid={`des-prov-${i}`}>
                    <span className="truncate">{p.proveedor_nombre}</span>
                    <span className="font-medium text-agro-yellow">{fmtL(p.monto)} <span className="text-[10px] text-text-muted">({p.porcentaje}%)</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3 text-[10px] text-text-muted border-t border-border pt-2">
            Factor aplicado: {(desgloseModal.factor_aplicado * 100).toFixed(2)}% del total facturado ({fmtL(desgloseModal.factura_total)})
            {desgloseModal.facturas?.length > 1 && <> · {desgloseModal.facturas.length} facturas cubiertas</>}
            {desgloseModal.guardado && <> · desglose guardado al registrar</>}
          </div>
          {desgloseModal.lineas?.length > 0 && (
            <div className="mt-3 max-h-56 overflow-y-auto border border-border/40 rounded">
              <table className="w-full text-xs">
                <thead className="bg-sidebar text-[10px] uppercase text-text-muted">
                  <tr className="text-left"><th className="p-1.5">Factura</th><th className="p-1.5">Producto</th><th className="p-1.5">Nivel</th><th className="p-1.5">Proveedor</th><th className="p-1.5 text-right">Monto</th></tr>
                </thead>
                <tbody>
                  {desgloseModal.lineas.map((l, i) => (
                    <tr key={i} className="border-t border-border/30">
                      <td className="p-1.5 font-mono text-agro-green">{l.factura_numero}</td>
                      <td className="p-1.5 truncate max-w-[160px]">{l.codigo} · {l.categoria}</td>
                      <td className="p-1.5">{l.nivel_aplicado || "—"}</td>
                      <td className="p-1.5 truncate max-w-[140px]">{l.proveedor_nombre}</td>
                      <td className="p-1.5 text-right">{fmtL(l.monto)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {viewerModal && (
        <Modal title="Comprobantes" onClose={() => setViewerModal(null)} testid="viewer-modal" wide>
          <div className="grid grid-cols-1 gap-3">
            {viewerModal.comps.map((c, i) => (
              <div key={i} className="border border-border rounded-md p-3">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-xs">
                    <strong>{c.descripcion || "Comprobante"}</strong>
                    <div className="text-[10px] text-text-muted">{fmtDateTime(c.uploaded_at)} · por {c.uploaded_by}</div>
                  </div>
                  <a href={c.url} download={`comprobante-${viewerModal.pago.factura_numero}-${i}.${c.url.includes('pdf') ? 'pdf' : 'jpg'}`} className="text-xs text-agro-green hover:underline flex items-center gap-1">
                    <EyeIcon size={12} /> Descargar
                  </a>
                </div>
                {c.url?.startsWith("data:image") ? (
                  <img src={c.url} alt="" className="w-full max-h-[60vh] object-contain rounded" />
                ) : (
                  <iframe src={c.url} className="w-full h-[60vh] rounded border border-border" title="PDF" />
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

function SubTab({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px ${active ? "border-agro-green text-text-primary" : "border-transparent text-text-muted hover:text-text-secondary"}`}>
      <Icon size={14} /> {children}
    </button>
  );
}

function EstadoBadge({ estado, motivo }) {
  const map = {
    pendiente: { c: "border-agro-yellow/40 bg-agro-yellow/10 text-agro-yellow", l: "Pendiente de aprobación" },
    autorizado: { c: "border-agro-green/40 bg-agro-green/10 text-agro-green", l: "Autorizado" },
    rechazado: { c: "border-red-500/40 bg-red-500/10 text-red-400", l: "Rechazado" },
    borrador: { c: "border-border bg-white/5 text-text-secondary", l: "Borrador" },
    devuelto: { c: "border-orange-500/40 bg-orange-500/10 text-orange-400", l: "Devuelto" },
    aplicado: { c: "border-blue-500/40 bg-blue-500/10 text-blue-400", l: "Aplicado" },
  };
  const m = map[estado] || map.pendiente;
  return (
    <div>
      <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${m.c}`}>{m.l}</span>
      {["rechazado", "devuelto"].includes(estado) && motivo && (
        <div className="text-[10px] text-red-300 mt-0.5 max-w-[180px] truncate" title={motivo}>{motivo}</div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, wide, testid, m: mExt }) {
  const mLocal = useModalForm({ onClose: mExt ? undefined : onClose, abierto: !mExt });
  const m = mExt || mLocal;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className={`bg-card border border-border rounded-lg w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
        data-testid={testid}>
        <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <h3 className="font-heading text-base font-semibold">{title}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1 block">{children}</label>;
const InfoRow = ({ l, v, highlight }) => (
  <div className="flex justify-between border-b border-border/40 py-1">
    <span className="text-xs text-text-muted">{l}</span>
    <span className={`text-xs ${highlight ? "text-agro-green font-semibold text-base" : "text-text-primary"}`}>{v}</span>
  </div>
);
